<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  estimateTravel,
  geocodePostcode,
  getAccommodations,
  getTransportModes,
  getWorker,
  getWorkLocations,
  listWorkers,
  removeWorkerAddress,
  saveAccommodations,
  saveTransportModes,
  saveWorkerAddress,
  saveWorkLocations,
  type Accommodation,
  type RiskLevel,
  type TransportMode,
  type TravelEstimate,
  type WorkerAddress,
  type WorkLocation,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";

const queryClient = useQueryClient();
const { data, isPending, isError, error } = useQuery({
  queryKey: ["accommodations"],
  queryFn: () => getAccommodations(),
});

// ---- Transport modes ----
const { data: modesData } = useQuery({
  queryKey: ["transport-modes"],
  queryFn: () => getTransportModes(),
});
const modes = ref<TransportMode[]>([]);
watch(
  modesData,
  (d) => {
    if (d) modes.value = d.modes.map((m) => ({ ...m }));
  },
  { immediate: true },
);
function addMode(): void {
  modes.value = [
    ...modes.value,
    { id: crypto.randomUUID(), name: "", speed_mph: 35, cost_per_mile: 0.45, active: true },
  ];
}
function removeMode(id: string): void {
  modes.value = modes.value.filter((m) => m.id !== id);
}
const modesSaving = ref(false);
const modesMsg = ref<string | null>(null);
async function saveModes(): Promise<void> {
  modesSaving.value = true;
  modesMsg.value = null;
  try {
    await saveTransportModes(modes.value.filter((m) => m.name.trim()));
    await queryClient.invalidateQueries({ queryKey: ["transport-modes"] });
    modesMsg.value = "Saved.";
  } finally {
    modesSaving.value = false;
  }
}

const rows = ref<Accommodation[]>([]);
watch(
  data,
  (d) => {
    if (d) rows.value = d.accommodations.map((a) => ({ ...a }));
  },
  { immediate: true },
);

function blank(): Accommodation {
  return {
    id: crypto.randomUUID(),
    name: "", line1: "", line2: "", city: "", postcode: "",
    latitude: null, longitude: null,
    contact_phone: "", contact_email: "", notes: "", active: true,
  };
}
function addRow(): void {
  rows.value = [...rows.value, blank()];
}
function removeRow(id: string): void {
  rows.value = rows.value.filter((r) => r.id !== id);
}

const geoBusy = ref<string | null>(null);
async function lookup(r: Accommodation): Promise<void> {
  if (!r.postcode.trim()) return;
  geoBusy.value = r.id;
  try {
    const res = await geocodePostcode(r.postcode.trim());
    r.latitude = res.latitude;
    r.longitude = res.longitude;
  } finally {
    geoBusy.value = null;
  }
}

const saving = ref(false);
const savedMsg = ref<string | null>(null);
async function save(): Promise<void> {
  saving.value = true;
  savedMsg.value = null;
  try {
    // Geocode any rows still missing coordinates.
    for (const r of rows.value) {
      if (r.latitude == null && r.postcode.trim()) await lookup(r);
    }
    await saveAccommodations(rows.value.filter((r) => r.name.trim() && r.postcode.trim()));
    await queryClient.invalidateQueries({ queryKey: ["accommodations"] });
    savedMsg.value = "Saved.";
  } finally {
    saving.value = false;
  }
}

// ---- Work locations (reusable work sites with a risk weighting) ----
const { data: wlData } = useQuery({
  queryKey: ["work-locations"],
  queryFn: () => getWorkLocations(),
});
const locations = ref<WorkLocation[]>([]);
watch(
  wlData,
  (d) => {
    if (d) locations.value = d.locations.map((l) => ({ ...l }));
  },
  { immediate: true },
);

const RISK_LEVELS: RiskLevel[] = ["low", "medium", "high"];
const RISK_CLS: Record<RiskLevel, string> = {
  low: "bg-semantic-success/10 text-semantic-success",
  medium: "bg-semantic-warning/10 text-semantic-warning",
  high: "bg-semantic-danger/10 text-semantic-danger",
};

function blankLocation(): WorkLocation {
  return {
    id: crypto.randomUUID(),
    name: "", ref: "", line1: "", line2: "", city: "", postcode: "",
    latitude: null, longitude: null,
    risk_level: "medium", risk_multiplier: 1.0, geofence_radius_m: null,
    site_hours_start: null, site_hours_end: null, active: true,
  };
}

const editing = ref<WorkLocation | null>(null);
const editingIsNew = ref(false);
const wlSaving = ref(false);
const wlGeoBusy = ref(false);
const wlMsg = ref<string | null>(null);

function addLocation(): void {
  editing.value = blankLocation();
  editingIsNew.value = true;
}
function editLocation(l: WorkLocation): void {
  editing.value = { ...l };
  editingIsNew.value = false;
}
function cancelEdit(): void {
  editing.value = null;
}
async function lookupEditing(): Promise<void> {
  const d = editing.value;
  if (!d || !d.postcode.trim()) return;
  wlGeoBusy.value = true;
  try {
    const res = await geocodePostcode(d.postcode.trim());
    d.latitude = res.latitude;
    d.longitude = res.longitude;
  } finally {
    wlGeoBusy.value = false;
  }
}
async function persistLocations(next: WorkLocation[]): Promise<void> {
  wlSaving.value = true;
  wlMsg.value = null;
  try {
    await saveWorkLocations(next.filter((l) => l.name.trim() && l.postcode.trim()));
    await queryClient.invalidateQueries({ queryKey: ["work-locations"] });
  } finally {
    wlSaving.value = false;
  }
}
async function saveEditing(): Promise<void> {
  const d = editing.value;
  if (!d || !d.name.trim() || !d.postcode.trim()) return;
  if (d.latitude == null && d.postcode.trim()) await lookupEditing();
  // Empty optional inputs come through as "" / NaN — normalise to null so the
  // payload validates (HH:MM-or-null, number-or-null).
  d.site_hours_start = d.site_hours_start || null;
  d.site_hours_end = d.site_hours_end || null;
  d.geofence_radius_m =
    typeof d.geofence_radius_m === "number" && !Number.isNaN(d.geofence_radius_m)
      ? d.geofence_radius_m
      : null;
  const next = editingIsNew.value
    ? [...locations.value, d]
    : locations.value.map((l) => (l.id === d.id ? d : l));
  locations.value = next;
  await persistLocations(next);
  editing.value = null;
  wlMsg.value = "Saved.";
}
async function deleteLocation(l: WorkLocation): Promise<void> {
  if (!confirm(`Delete work location "${l.name}"?`)) return;
  const next = locations.value.filter((x) => x.id !== l.id);
  locations.value = next;
  await persistLocations(next);
}

// ---- Calculate travel time ----
const { data: workersList } = useQuery({
  queryKey: ["workers", true],
  queryFn: () => listWorkers(true),
});
const selectedWorkerId = ref<string>("");
const { data: workerDetail } = useQuery({
  queryKey: ["worker", selectedWorkerId],
  queryFn: () => getWorker(selectedWorkerId.value),
  enabled: () => !!selectedWorkerId.value,
});
const workerAddresses = computed(() => workerDetail.value?.addresses ?? []);
const startAddressId = ref<string>("");
const startAddress = computed(
  () => workerAddresses.value.find((a) => a.id === startAddressId.value) ?? null,
);
// Reset the chosen address whenever the worker changes.
watch(selectedWorkerId, () => {
  startAddressId.value = "";
  calcResult.value = null;
});

const destLocationId = ref<string>("");
const activeWorkLocations = computed(() =>
  (wlData.value?.locations ?? []).filter((l) => l.active),
);
const destLocation = computed(
  () => activeWorkLocations.value.find((l) => l.id === destLocationId.value) ?? null,
);

const activeModes = computed(() => (modesData.value?.modes ?? []).filter((m) => m.active));
const calcModeId = ref<string>("");
watch(
  activeModes,
  (m) => {
    if (!calcModeId.value && m.length) calcModeId.value = m[0].id;
  },
  { immediate: true },
);

const returnJourney = ref(false);
const calcBusy = ref(false);
const calcError = ref<string | null>(null);
const calcResult = ref<TravelEstimate | null>(null);

function addrLabel(a: { type: string; label: string | null; line1: string | null; postcode: string; latitude: number | null }): string {
  const head = a.label ? `${a.type} — ${a.label}` : a.type;
  const where = [a.line1, a.postcode].filter(Boolean).join(", ");
  return `${head}${where ? ` · ${where}` : ""}${a.latitude == null ? " (no coords)" : ""}`;
}
const canCalc = computed(
  () =>
    startAddress.value?.latitude != null &&
    destLocation.value?.latitude != null &&
    !!calcModeId.value,
);
async function calculate(): Promise<void> {
  if (!startAddress.value || !destLocation.value) return;
  if (startAddress.value.latitude == null || destLocation.value.latitude == null) return;
  calcBusy.value = true;
  calcError.value = null;
  calcResult.value = null;
  try {
    calcResult.value = await estimateTravel({
      origin_lat: startAddress.value.latitude,
      origin_lng: startAddress.value.longitude as number,
      dest_lat: destLocation.value.latitude,
      dest_lng: destLocation.value.longitude as number,
      mode_id: calcModeId.value,
      return_journey: returnJourney.value,
    });
  } catch (e) {
    calcError.value = e instanceof Error ? e.message : String(e);
  } finally {
    calcBusy.value = false;
  }
}
function fmtMins(n: number): string {
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}

// ---- Worker addresses / accommodation (per selected worker) ----
type AddrTab = "home" | "hotel" | "alternate" | "accommodation";
const ADDRESS_TYPES = [
  { value: "home", label: "Home" },
  { value: "hotel", label: "Hotel / lodging" },
  { value: "alternate", label: "Alternate" },
];
const ADDR_TABS: { key: AddrTab; label: string; icon: string }[] = [
  { key: "home", label: "Home address", icon: "🏠" },
  { key: "hotel", label: "Hotel / lodging", icon: "🏨" },
  { key: "alternate", label: "Alternate address", icon: "📍" },
  { key: "accommodation", label: "Accommodations", icon: "🛏" },
];
const addrTab = ref<AddrTab>("home");
// Non-overlapping buckets: catalogue-linked addresses are "Accommodations";
// otherwise grouped by their type.
function bucketOf(a: WorkerAddress): AddrTab {
  if (a.accommodation_id) return "accommodation";
  if (a.type === "hotel") return "hotel";
  if (a.type === "alternate") return "alternate";
  return "home";
}
const addrTabsWithCount = computed(() =>
  ADDR_TABS.map((t) => ({
    ...t,
    count: workerAddresses.value.filter((a) => bucketOf(a) === t.key).length,
  })),
);
const tabAddresses = computed(() =>
  workerAddresses.value.filter((a) => bucketOf(a) === addrTab.value),
);

function blankAddr(): Omit<WorkerAddress, "id"> & { id?: string } {
  return {
    type: "home", label: "", line1: "", line2: "", city: "", postcode: "",
    latitude: null, longitude: null, is_primary: false, accommodation_id: null,
  };
}
const addrDraft = reactive(blankAddr());
const showAddr = ref(false);
const editingAddrIsNew = ref(true);
const addrBusy = ref(false);
const addrGeoBusy = ref(false);
const addrGeoMsg = ref<string | null>(null);

const activeAccommodations = computed(() =>
  (data.value?.accommodations ?? []).filter((a) => a.active),
);
function pickAccommodation(accId: string): void {
  const a = activeAccommodations.value.find((x) => x.id === accId);
  if (!a) return;
  addrDraft.label = a.name;
  addrDraft.line1 = a.line1;
  addrDraft.line2 = a.line2;
  addrDraft.city = a.city;
  addrDraft.postcode = a.postcode;
  addrDraft.latitude = a.latitude;
  addrDraft.longitude = a.longitude;
  addrDraft.accommodation_id = a.id;
  addrGeoMsg.value = a.latitude != null ? "Coordinates from the catalogue." : null;
}
function startAddAddr(): void {
  Object.assign(addrDraft, blankAddr());
  addrDraft.type = addrTab.value === "accommodation" ? "hotel" : addrTab.value;
  editingAddrIsNew.value = true;
  addrGeoMsg.value = null;
  showAddr.value = true;
}
function editAddr(a: WorkerAddress): void {
  Object.assign(addrDraft, a);
  editingAddrIsNew.value = false;
  addrGeoMsg.value =
    a.latitude != null
      ? `Coordinates on file (${a.latitude.toFixed(2)}, ${a.longitude?.toFixed(2)})`
      : null;
  showAddr.value = true;
}
function cancelAddr(): void {
  showAddr.value = false;
  Object.assign(addrDraft, blankAddr());
  addrGeoMsg.value = null;
}
async function geocodeAddr(): Promise<void> {
  if (!addrDraft.postcode.trim()) return;
  addrGeoBusy.value = true;
  addrGeoMsg.value = null;
  try {
    const r = await geocodePostcode(addrDraft.postcode.trim());
    if (r.latitude != null && r.longitude != null) {
      addrDraft.latitude = r.latitude;
      addrDraft.longitude = r.longitude;
      addrGeoMsg.value = `Coordinates set (${r.latitude.toFixed(2)}, ${r.longitude.toFixed(2)})`;
    } else {
      addrGeoMsg.value = "Couldn't resolve that postcode — check it and retry.";
    }
  } finally {
    addrGeoBusy.value = false;
  }
}
async function refreshWorker(): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ["worker", selectedWorkerId.value] });
}
async function saveAddr(): Promise<void> {
  if (!selectedWorkerId.value || !addrDraft.postcode.trim()) return;
  addrBusy.value = true;
  try {
    if (addrDraft.latitude == null) await geocodeAddr();
    await saveWorkerAddress(selectedWorkerId.value, { ...addrDraft });
    await refreshWorker();
    cancelAddr();
  } finally {
    addrBusy.value = false;
  }
}
async function dropAddr(a: WorkerAddress): Promise<void> {
  if (!selectedWorkerId.value) return;
  if (!confirm(`Remove this ${a.type} address?`)) return;
  await removeWorkerAddress(selectedWorkerId.value, a.id);
  await refreshWorker();
}

const inputCls = "w-full rounded-md border border-neutral-300 px-2 py-1 text-sm";
</script>

<template>
  <PageLayout wide>
    <h1 class="mb-4 text-xl font-semibold">
      Travel &amp; accommodation
    </h1>

    <!-- Calculate travel time -->
    <section class="mb-8 rounded-xl border border-neutral-200 bg-neutral-0 p-5">
      <h2 class="mb-3 font-semibold">
        📍 Calculate travel time
      </h2>
      <div class="grid gap-3 sm:grid-cols-3">
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Worker</span>
          <select
            v-model="selectedWorkerId"
            :class="inputCls"
          >
            <option value="">
              Select worker…
            </option>
            <option
              v-for="w in workersList ?? []"
              :key="w.id"
              :value="w.id"
            >
              {{ w.name }}
            </option>
          </select>
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Starting from</span>
          <select
            v-model="startAddressId"
            :disabled="!selectedWorkerId || !workerAddresses.length"
            :class="inputCls"
          >
            <option value="">
              {{ selectedWorkerId && !workerAddresses.length ? "No addresses on file" : "Select address…" }}
            </option>
            <option
              v-for="a in workerAddresses"
              :key="a.id"
              :value="a.id"
              :disabled="a.latitude == null"
            >
              {{ addrLabel(a) }}
            </option>
          </select>
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Work location</span>
          <select
            v-model="destLocationId"
            :class="inputCls"
          >
            <option value="">
              Select location…
            </option>
            <option
              v-for="l in activeWorkLocations"
              :key="l.id"
              :value="l.id"
              :disabled="l.latitude == null"
            >
              {{ l.name }}{{ l.ref ? ` (${l.ref})` : "" }}{{ l.latitude == null ? " — no coords" : "" }}
            </option>
          </select>
        </label>
        <label class="block sm:col-span-2">
          <span class="mb-1 block text-xs text-neutral-500">Transport mode</span>
          <select
            v-model="calcModeId"
            :class="inputCls"
          >
            <option
              v-for="m in activeModes"
              :key="m.id"
              :value="m.id"
            >
              {{ m.name }} ({{ m.speed_mph }} mph<template v-if="m.cost_per_mile">, £{{ m.cost_per_mile }}/mile</template>)
            </option>
          </select>
        </label>
        <label class="flex items-center gap-2 self-end pb-1 text-sm">
          <input
            v-model="returnJourney"
            type="checkbox"
            class="rounded border-neutral-300"
          >
          Include return journey
        </label>
      </div>
      <div class="mt-3 flex items-center gap-3">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="!canCalc || calcBusy"
          @click="calculate"
        >
          {{ calcBusy ? "Calculating…" : "Calculate travel time" }}
        </button>
        <span
          v-if="calcError"
          class="text-sm text-semantic-danger"
        >{{ calcError }}</span>
      </div>

      <!-- Result -->
      <div
        v-if="calcResult"
        class="mt-4 grid gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4 sm:grid-cols-4"
      >
        <div>
          <p class="text-xs text-neutral-500">
            Distance (one way)
          </p>
          <p class="text-lg font-semibold">
            {{ calcResult.distance_miles }} mi
          </p>
        </div>
        <div>
          <p class="text-xs text-neutral-500">
            Travel time
          </p>
          <p class="text-lg font-semibold">
            {{ fmtMins(calcResult.travel_to_minutes) }}
            <span
              v-if="calcResult.travel_from_minutes"
              class="text-sm font-normal text-neutral-500"
            >each way</span>
          </p>
        </div>
        <div>
          <p class="text-xs text-neutral-500">
            Total travel
          </p>
          <p class="text-lg font-semibold">
            {{ fmtMins(calcResult.total_travel_minutes) }}
          </p>
        </div>
        <div>
          <p class="text-xs text-neutral-500">
            Mileage cost
          </p>
          <p class="text-lg font-semibold">
            £{{ calcResult.cost.toFixed(2) }}
          </p>
        </div>
        <p class="text-xs text-neutral-400 sm:col-span-4">
          {{ calcResult.mode_name }}{{ returnJourney ? " · return journey" : " · one way" }}.
          Estimate from great-circle distance × road factor at the mode's
          average speed (no live traffic).
        </p>
      </div>
    </section>

    <!-- Worker addresses / accommodation -->
    <section class="mb-8 rounded-xl border border-neutral-200 bg-neutral-0 p-5">
      <div class="mb-1 flex items-center justify-between gap-3">
        <h2 class="font-semibold">
          🏠 Worker addresses / accommodation
        </h2>
        <button
          type="button"
          class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="!selectedWorkerId"
          @click="startAddAddr"
        >
          + Add address / accommodation
        </button>
      </div>
      <p class="mb-3 text-xs text-neutral-400">
        Addresses for the worker selected above — used as travel start points.
        Catalogue-linked lodging appears under <strong>Accommodations</strong>.
      </p>

      <p
        v-if="!selectedWorkerId"
        class="rounded-lg border border-neutral-200 p-6 text-sm text-neutral-500"
      >
        Select a worker in <strong>Calculate travel time</strong> above to see
        and manage their addresses.
      </p>
      <template v-else>
        <!-- Tabs -->
        <div class="mb-4 flex flex-wrap gap-1 border-b border-neutral-200">
          <button
            v-for="t in addrTabsWithCount"
            :key="t.key"
            type="button"
            class="-mb-px border-b-2 px-3 py-2 text-sm"
            :class="addrTab === t.key
              ? 'border-brand-primary font-medium text-brand-primary'
              : 'border-transparent text-neutral-500 hover:text-neutral-900'"
            @click="addrTab = t.key"
          >
            {{ t.icon }} {{ t.label }}
            <span class="ml-1 rounded-full bg-neutral-100 px-1.5 text-xs text-neutral-500">{{ t.count }}</span>
          </button>
        </div>

        <!-- Add / edit form -->
        <div
          v-if="showAddr"
          class="mb-4 grid gap-2 rounded-xl border border-brand-primary/30 bg-brand-primary/5 p-4 sm:grid-cols-3"
        >
          <label class="block">
            <span class="mb-1 block text-xs text-neutral-500">Type</span>
            <select
              v-model="addrDraft.type"
              :class="inputCls"
            >
              <option
                v-for="t in ADDRESS_TYPES"
                :key="t.value"
                :value="t.value"
              >
                {{ t.label }}
              </option>
            </select>
          </label>
          <label class="block sm:col-span-2">
            <span class="mb-1 block text-xs text-neutral-500">Label (optional)</span>
            <input
              v-model="addrDraft.label"
              placeholder="e.g. Premier Inn Aberdeen"
              :class="inputCls"
            >
          </label>
          <label
            v-if="addrDraft.type === 'hotel' && activeAccommodations.length"
            class="block sm:col-span-3"
          >
            <span class="mb-1 block text-xs text-neutral-500">From accommodation catalogue</span>
            <select
              :class="inputCls"
              @change="pickAccommodation(($event.target as HTMLSelectElement).value)"
            >
              <option value="">
                Pick a saved hotel…
              </option>
              <option
                v-for="a in activeAccommodations"
                :key="a.id"
                :value="a.id"
              >
                {{ a.name }}{{ a.city ? ` — ${a.city}` : "" }}
              </option>
            </select>
          </label>
          <label class="block sm:col-span-2">
            <span class="mb-1 block text-xs text-neutral-500">Address line 1</span>
            <input
              v-model="addrDraft.line1"
              :class="inputCls"
            >
          </label>
          <label class="block">
            <span class="mb-1 block text-xs text-neutral-500">City</span>
            <input
              v-model="addrDraft.city"
              :class="inputCls"
            >
          </label>
          <label class="block">
            <span class="mb-1 block text-xs text-neutral-500">Postcode</span>
            <div class="flex gap-2">
              <input
                v-model="addrDraft.postcode"
                placeholder="e.g. AB12 4QS"
                :class="inputCls"
              >
              <button
                type="button"
                class="shrink-0 rounded-md border border-neutral-300 px-3 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
                :disabled="addrGeoBusy || !addrDraft.postcode.trim()"
                @click="geocodeAddr"
              >
                {{ addrGeoBusy ? "…" : "Lookup" }}
              </button>
            </div>
            <span
              v-if="addrGeoMsg"
              class="mt-1 block text-xs"
              :class="addrDraft.latitude != null ? 'text-semantic-success' : 'text-semantic-warning'"
            >{{ addrGeoMsg }}</span>
          </label>
          <label class="flex items-center gap-2 self-end pb-2 text-sm">
            <input
              v-model="addrDraft.is_primary"
              type="checkbox"
              class="rounded border-neutral-300"
            >
            Primary {{ addrDraft.type }} address
          </label>
          <div class="flex items-center gap-3 sm:col-span-3">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="addrBusy || !addrDraft.postcode.trim()"
              @click="saveAddr"
            >
              {{ addrBusy ? "Saving…" : (editingAddrIsNew ? "Add" : "Save changes") }}
            </button>
            <button
              type="button"
              class="text-sm text-neutral-500 hover:text-neutral-900"
              @click="cancelAddr"
            >
              Cancel
            </button>
          </div>
        </div>

        <!-- Cards -->
        <p
          v-if="!tabAddresses.length"
          class="rounded-lg border border-neutral-200 p-6 text-sm text-neutral-500"
        >
          No {{ addrTabsWithCount.find((t) => t.key === addrTab)?.label.toLowerCase() }} on file.
        </p>
        <div
          v-else
          class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          <div
            v-for="a in tabAddresses"
            :key="a.id"
            class="rounded-xl border p-4"
            :class="a.is_primary ? 'border-semantic-success/50 bg-semantic-success/5' : 'border-neutral-200'"
          >
            <div class="mb-1 flex items-start justify-between gap-2">
              <p class="font-medium capitalize">
                {{ a.label || a.type }}
                <span
                  v-if="a.is_primary"
                  class="ml-1 rounded-full bg-semantic-success/10 px-2 py-0.5 text-xs font-normal text-semantic-success"
                >Primary</span>
              </p>
              <div class="flex shrink-0 gap-1">
                <button
                  type="button"
                  class="rounded-md border border-neutral-300 px-2 py-0.5 text-xs hover:bg-neutral-50"
                  title="Edit"
                  @click="editAddr(a)"
                >
                  ✎
                </button>
                <button
                  type="button"
                  class="rounded-md border border-neutral-300 px-2 py-0.5 text-xs text-semantic-danger hover:bg-semantic-danger/5"
                  title="Remove"
                  @click="dropAddr(a)"
                >
                  🗑
                </button>
              </div>
            </div>
            <p class="text-sm text-neutral-600">
              <template v-if="a.line1">
                {{ a.line1 }}<br>
              </template>
              <template v-if="a.line2">
                {{ a.line2 }}<br>
              </template>
              <template v-if="a.city">
                {{ a.city }}<br>
              </template>
              {{ a.postcode }}
            </p>
            <p
              v-if="a.latitude != null"
              class="mt-2 text-xs text-neutral-400"
            >
              📍 {{ a.latitude.toFixed(4) }}, {{ a.longitude?.toFixed(4) }}
            </p>
            <p
              v-else
              class="mt-2 text-xs text-semantic-warning"
            >
              No coordinates — won't work as a travel start point.
            </p>
          </div>
        </div>
      </template>
    </section>

    <!-- Work locations -->
    <section class="mb-8 rounded-xl border border-neutral-200 bg-neutral-0 p-5">
      <div class="mb-1 flex items-center justify-between gap-3">
        <h2 class="font-semibold">
          Work locations
        </h2>
        <button
          type="button"
          class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-semibold text-brand-primary-on"
          @click="addLocation"
        >
          + Add location
        </button>
      </div>
      <p class="mb-3 text-xs text-neutral-400">
        Reusable work sites with a risk weighting. The
        <strong>multiplier</strong> scales a worker's fatigue Risk Index for
        shifts at that site (1.00× = no change); the site
        <strong>ref</strong> joins to the roster's project code.
      </p>

      <!-- Add / edit form -->
      <div
        v-if="editing"
        class="mb-4 grid gap-2 rounded-xl border border-brand-primary/30 bg-brand-primary/5 p-4 sm:grid-cols-3"
      >
        <label class="block sm:col-span-2">
          <span class="mb-1 block text-xs text-neutral-500">Name</span>
          <input
            v-model="editing.name"
            placeholder="e.g. Aberdeen Station"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Ref / site code</span>
          <input
            v-model="editing.ref"
            placeholder="e.g. ABD"
            :class="inputCls"
          >
        </label>
        <label class="block sm:col-span-2">
          <span class="mb-1 block text-xs text-neutral-500">Address line 1</span>
          <input
            v-model="editing.line1"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">City</span>
          <input
            v-model="editing.city"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Postcode</span>
          <div class="flex gap-2">
            <input
              v-model="editing.postcode"
              placeholder="e.g. AB11 6NA"
              :class="inputCls"
            >
            <button
              type="button"
              class="shrink-0 rounded-md border border-neutral-300 px-3 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="wlGeoBusy || !editing.postcode.trim()"
              @click="lookupEditing"
            >
              {{ wlGeoBusy ? "…" : "Lookup" }}
            </button>
          </div>
          <span
            v-if="editing.latitude != null"
            class="mt-1 block text-xs text-semantic-success"
          >Geocoded ({{ editing.latitude.toFixed(4) }}, {{ editing.longitude?.toFixed(4) }})</span>
          <span
            v-else
            class="mt-1 block text-xs text-semantic-warning"
          >No coordinates yet</span>
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Risk level</span>
          <select
            v-model="editing.risk_level"
            :class="inputCls"
          >
            <option
              v-for="r in RISK_LEVELS"
              :key="r"
              :value="r"
            >
              {{ r }}
            </option>
          </select>
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Risk multiplier</span>
          <input
            v-model.number="editing.risk_multiplier"
            type="number"
            min="0.1"
            max="5"
            step="0.05"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Geofence radius (m)</span>
          <input
            v-model.number="editing.geofence_radius_m"
            type="number"
            min="10"
            max="50000"
            step="50"
            placeholder="default 1000"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Site hours from</span>
          <input
            v-model="editing.site_hours_start"
            type="time"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Site hours to</span>
          <input
            v-model="editing.site_hours_end"
            type="time"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Active</span>
          <select
            v-model="editing.active"
            :class="inputCls"
          >
            <option :value="true">
              Active
            </option>
            <option :value="false">
              Inactive
            </option>
          </select>
        </label>
        <div class="flex items-center gap-3 sm:col-span-3">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="wlSaving || !editing.name.trim() || !editing.postcode.trim()"
            @click="saveEditing"
          >
            {{ wlSaving ? "Saving…" : (editingIsNew ? "Add location" : "Save changes") }}
          </button>
          <button
            type="button"
            class="text-sm text-neutral-500 hover:text-neutral-900"
            @click="cancelEdit"
          >
            Cancel
          </button>
        </div>
      </div>

      <p
        v-if="!locations.length"
        class="rounded-lg border border-neutral-200 p-6 text-sm text-neutral-500"
      >
        No work locations yet. Add one to use it as a travel destination and to
        risk-weight fatigue at that site.
      </p>
      <div
        v-else
        class="overflow-hidden rounded-lg border border-neutral-200"
      >
        <table class="w-full text-left text-sm">
          <thead class="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-400">
            <tr>
              <th class="px-3 py-2 font-medium">
                Name
              </th>
              <th class="px-3 py-2 font-medium">
                Address
              </th>
              <th class="px-3 py-2 font-medium">
                Risk level
              </th>
              <th class="px-3 py-2 font-medium">
                Multiplier
              </th>
              <th class="px-3 py-2 font-medium">
                Coordinates
              </th>
              <th class="px-3 py-2 text-right font-medium">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="l in locations"
              :key="l.id"
              class="border-t border-neutral-100"
              :class="{ 'opacity-50': !l.active }"
            >
              <td class="px-3 py-2">
                <span class="font-medium">{{ l.name }}</span>
                <span
                  v-if="l.ref"
                  class="block text-xs text-neutral-400"
                >{{ l.ref }}</span>
              </td>
              <td class="px-3 py-2 text-neutral-500">
                {{ [l.line1, l.city].filter(Boolean).join(", ") || "—" }}
                <span
                  v-if="l.postcode"
                  class="block text-xs text-neutral-400"
                >{{ l.postcode }}</span>
              </td>
              <td class="px-3 py-2">
                <span
                  class="rounded-full px-2 py-0.5 text-xs"
                  :class="RISK_CLS[l.risk_level]"
                >{{ l.risk_level }}</span>
              </td>
              <td class="px-3 py-2">
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="l.risk_multiplier > 1 ? 'bg-semantic-warning/10 text-semantic-warning' : 'bg-semantic-success/10 text-semantic-success'"
                >{{ l.risk_multiplier.toFixed(2) }}×</span>
              </td>
              <td class="px-3 py-2 text-xs text-neutral-500">
                <template v-if="l.latitude != null">
                  {{ l.latitude.toFixed(4) }}, {{ l.longitude?.toFixed(4) }}
                </template>
                <span
                  v-else
                  class="text-semantic-warning"
                >No coords</span>
              </td>
              <td class="px-3 py-2 text-right">
                <button
                  type="button"
                  class="rounded-md border border-neutral-300 px-2 py-1 text-xs hover:bg-neutral-50"
                  title="Edit"
                  @click="editLocation(l)"
                >
                  ✎
                </button>
                <button
                  type="button"
                  class="ml-1 rounded-md border border-neutral-300 px-2 py-1 text-xs text-semantic-danger hover:bg-semantic-danger/5"
                  title="Delete"
                  @click="deleteLocation(l)"
                >
                  🗑
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <span
        v-if="wlMsg"
        class="mt-2 block text-sm text-semantic-success"
      >{{ wlMsg }}</span>
    </section>

    <!-- Transport modes -->
    <section class="mb-8 rounded-xl border border-neutral-200 bg-neutral-0 p-5">
      <div class="mb-1 flex items-center justify-between">
        <h2 class="font-semibold">
          Transport modes
        </h2>
        <button
          type="button"
          class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
          @click="addMode"
        >
          + Add mode
        </button>
      </div>
      <p class="mb-3 text-xs text-neutral-400">
        Average speeds the travel-time engine uses. Defaults shown until you
        save your own.
      </p>
      <div
        v-for="m in modes"
        :key="m.id"
        class="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-5"
      >
        <input
          v-model="m.name"
          placeholder="Name (e.g. Crew van)"
          :class="[inputCls, 'sm:col-span-2']"
        >
        <label class="block">
          <input
            v-model.number="m.speed_mph"
            type="number"
            min="1"
            placeholder="mph"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <input
            v-model.number="m.cost_per_mile"
            type="number"
            min="0"
            step="0.01"
            placeholder="£/mile"
            :class="inputCls"
          >
        </label>
        <div class="flex items-center justify-between">
          <select
            v-model="m.active"
            :class="[inputCls, 'flex-1']"
          >
            <option :value="true">
              Active
            </option>
            <option :value="false">
              Inactive
            </option>
          </select>
          <button
            type="button"
            class="ml-2 text-xs text-neutral-400 hover:text-semantic-danger"
            @click="removeMode(m.id)"
          >
            ✕
          </button>
        </div>
      </div>
      <div class="mt-3 flex items-center gap-3">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="modesSaving"
          @click="saveModes"
        >
          {{ modesSaving ? "Saving…" : "Save modes" }}
        </button>
        <span
          v-if="modesMsg"
          class="text-sm text-semantic-success"
        >{{ modesMsg }}</span>
      </div>
    </section>

    <div class="mb-1 flex items-center justify-between gap-3">
      <h2 class="font-semibold">
        Accommodation
      </h2>
      <button
        type="button"
        class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
        @click="addRow"
      >
        + Add accommodation
      </button>
    </div>
    <p class="mb-4 text-sm text-neutral-500">
      A reusable catalogue of hotels / lodging. Add one here, then attach it to a
      worker's hotel address on their profile — travel time is computed from the
      assigned accommodation when a worker is staying away.
    </p>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div v-else>
      <p
        v-if="!rows.length"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6 text-sm text-neutral-500"
      >
        No accommodation on file. Add a hotel to get started.
      </p>
      <div
        v-for="r in rows"
        :key="r.id"
        class="mb-3 grid gap-2 rounded-xl border border-neutral-200 bg-neutral-0 p-4 sm:grid-cols-3"
      >
        <label class="block sm:col-span-2">
          <span class="mb-1 block text-xs text-neutral-500">Name</span>
          <input
            v-model="r.name"
            placeholder="e.g. Premier Inn Glasgow"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Active</span>
          <select
            v-model="r.active"
            :class="inputCls"
          >
            <option :value="true">
              Active
            </option>
            <option :value="false">
              Inactive
            </option>
          </select>
        </label>
        <label class="block sm:col-span-2">
          <span class="mb-1 block text-xs text-neutral-500">Address line 1</span>
          <input
            v-model="r.line1"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">City</span>
          <input
            v-model="r.city"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Postcode</span>
          <div class="flex gap-2">
            <input
              v-model="r.postcode"
              placeholder="e.g. G1 1AA"
              :class="inputCls"
            >
            <button
              type="button"
              class="shrink-0 rounded-md border border-neutral-300 px-3 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="geoBusy === r.id || !r.postcode.trim()"
              @click="lookup(r)"
            >
              {{ geoBusy === r.id ? "…" : "Lookup" }}
            </button>
          </div>
          <span
            v-if="r.latitude != null"
            class="mt-1 block text-xs text-semantic-success"
          >Geocoded ({{ r.latitude.toFixed(2) }}, {{ r.longitude?.toFixed(2) }})</span>
          <span
            v-else
            class="mt-1 block text-xs text-semantic-warning"
          >No coordinates yet</span>
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Contact phone</span>
          <input
            v-model="r.contact_phone"
            :class="inputCls"
          >
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Contact email</span>
          <input
            v-model="r.contact_email"
            :class="inputCls"
          >
        </label>
        <div class="flex items-end">
          <button
            type="button"
            class="text-sm text-neutral-400 hover:text-semantic-danger"
            @click="removeRow(r.id)"
          >
            Remove
          </button>
        </div>
      </div>

      <div class="mt-4 flex items-center gap-3">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? "Saving…" : "Save accommodation" }}
        </button>
        <span
          v-if="savedMsg"
          class="text-sm text-semantic-success"
        >{{ savedMsg }}</span>
      </div>
    </div>
  </PageLayout>
</template>
